import { Plus, Network, Unplug, PlugZap, RefreshCw } from 'lucide-react';
import { useModalStore } from '@/stores/modal-store';
import { useConnectionStore } from '@/stores/connection-store';
import { useToastStore } from '@/stores/toast-store';
import { apiClient } from '@/api/client';
import type { Command } from '../types';

/**
 * Connection-level commands. "Connect to…" and "Disconnect from…" pop open
 * the command bar in search mode scoped to `@connection` so the user picks
 * which one — no inline list to maintain here.
 */
export const connectionCommands: Command[] = [
  {
    id: 'connection.new',
    label: 'New Connection',
    icon: Plus,
    category: 'Connection',
    keywords: ['add', 'database', 'create'],
    run: () => useModalStore.getState().openNewConnection(),
  },
  {
    id: 'connection.connect',
    label: 'Connect to Database...',
    icon: PlugZap,
    category: 'Connection',
    keywords: ['open', 'database'],
    // Re-enter the bar in search mode scoped to connections; picking one
    // will trigger its own connect action. Single source of truth lives
    // in the search provider, not duplicated here.
    run: () => {
      window.dispatchEvent(new CustomEvent('dbgo:command-bar:open', {
        detail: { mode: 'search', prefill: '@connection ' },
      }));
    },
  },
  {
    id: 'connection.disconnect-all',
    label: 'Disconnect All',
    icon: Unplug,
    category: 'Connection',
    keywords: ['close'],
    when: (ctx) => ctx.activeConnections.length > 0,
    run: async (ctx) => {
      let ok = 0;
      for (const id of ctx.activeConnections) {
        try {
          await apiClient.post(`/api/connections/${id}/disconnect`, {});
          useConnectionStore.getState().setStatus(id, 'disconnected');
          ok++;
        } catch {
          // Surface a single summary toast at the end rather than per-failure
        }
      }
      useToastStore.getState().addToast(
        'success',
        `Disconnected ${ok} of ${ctx.activeConnections.length} connection(s).`,
      );
    },
  },
  {
    id: 'connection.reload-schema',
    label: 'Reload Schema (Active Connection)',
    icon: RefreshCw,
    category: 'Connection',
    keywords: ['refresh', 'cache', 'invalidate'],
    when: (ctx) => ctx.activeConnectionId !== null,
    run: async (ctx) => {
      if (!ctx.activeConnectionId) return;
      try {
        await apiClient.get(`/api/schema/${ctx.activeConnectionId}?refresh=1`);
        useToastStore.getState().addToast('success', 'Schema reloaded.');
      } catch (err) {
        useToastStore.getState().addToast(
          'error',
          `Reload failed: ${err instanceof Error ? err.message : 'unknown error'}`,
        );
      }
    },
  },
  {
    id: 'tunnel.new',
    label: 'New Tunnel',
    icon: Network,
    category: 'Tunnel',
    keywords: ['ssh', 'add'],
    run: () => useModalStore.getState().openNewTunnel(),
  },
];
